import React, { useState, useRef } from "react";
import { useForm, router } from "@inertiajs/react";
import Layout from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useVideoUpload } from "../../hooks/useVideoUpload";

interface Props {
  direct_upload_url: string;
  errors?: string[];
}

export default function VideosNew({ direct_upload_url, errors }: Props) {
  const { data, setData, processing } = useForm({
    video_files: [] as File[],
  });

  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    uploadProgress,
    isUploading,
    uploadErrors,
    fileProgress,
    uploadFiles,
    validateFile,
  } = useVideoUpload({
    directUploadUrl: direct_upload_url,
    onSuccess: () => {
      router.visit("/videos");
    },
    onError: (errors) => {
      console.error("Upload failed:", errors);
    },
  });

  const handleFilesSelect = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];

    fileArray.forEach((file) => {
      const error = validateFile(file);
      if (!error) {
        validFiles.push(file);
      }
    });

    if (validFiles.length > 0) {
      setData("video_files", [...data.video_files, ...validFiles]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFilesSelect(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFilesSelect(files);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await uploadFiles(data.video_files);
  };

  const allErrors = [...(Array.isArray(errors) ? errors : []), ...uploadErrors];

  return (
    <Layout>
      <div className="mx-auto max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>Upload Video</CardTitle>
            <CardDescription>
              Upload your golf video (up to 10GB). Supported formats: MP4, MOV,
              AVI, WebM
            </CardDescription>
          </CardHeader>
          <CardContent>
            {allErrors.length > 0 && (
              <div className="px-4 py-3 mb-4 border bg-destructive/15 border-destructive text-destructive">
                <ul className="list-disc list-inside">
                  {allErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Video File</label>

                {/* Drag and Drop Area */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={openFileDialog}
                  className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                    isDragOver
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/25 hover:border-primary/50"
                  }`}
                >
                  <div className="space-y-2">
                    <div className="mx-auto w-12 h-12 text-muted-foreground">
                      <svg
                        className="w-full h-full"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {isDragOver
                          ? "Drop your videos here"
                          : "Drag and drop your videos here"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        or click to browse files (multiple files supported)
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      MP4, MOV, AVI, WebM up to 10GB each
                    </p>
                  </div>
                </div>

                {/* Hidden File Input */}
                <Input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  accept="video/*"
                  multiple
                  className="hidden"
                />

                {/* Selected Files Info */}
                {data.video_files.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-medium">
                        {data.video_files.length} file
                        {data.video_files.length !== 1 ? "s" : ""} selected
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setData("video_files", [])}
                      >
                        Clear All
                      </Button>
                    </div>
                    <div className="overflow-y-auto space-y-1 max-h-64">
                      {data.video_files.map((file, index) => {
                        const fileKey = `${file.name}-${index}`;
                        const progress = fileProgress[fileKey] || 0;
                        return (
                          <div
                            key={index}
                            className="px-2 py-1 text-sm bg-muted"
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {file.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {(file.size / 1000 / 1000).toFixed(2)} MB
                                </p>
                              </div>
                              {!isUploading && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newFiles = data.video_files.filter(
                                      (_, i) => i !== index,
                                    );
                                    setData("video_files", newFiles);
                                  }}
                                >
                                  ×
                                </Button>
                              )}
                            </div>
                            {isUploading && (
                              <div className="mt-1">
                                <div className="flex justify-between mb-0.5 text-xs">
                                  <span>Uploading...</span>
                                  <span>{progress}%</span>
                                </div>
                                <div className="w-full h-1 bg-secondary">
                                  <div
                                    className="h-1 transition-all duration-300 bg-primary"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {isUploading && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>
                      Uploading {data.video_files.length} video
                      {data.video_files.length !== 1 ? "s" : ""} to S3...
                    </span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full h-2 bg-secondary">
                    <div
                      className="h-2 transition-all duration-300 bg-primary"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={
                  processing || isUploading || data.video_files.length === 0
                }
                className="w-full"
              >
                {isUploading
                  ? `Uploading ${data.video_files.length} video${
                      data.video_files.length !== 1 ? "s" : ""
                    }...`
                  : processing
                  ? "Creating..."
                  : `Upload ${data.video_files.length || ""} Video${
                      data.video_files.length !== 1 ? "s" : ""
                    }`}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
